import _ from 'lodash';
import {useContext, useEffect, useRef, useState} from 'react';
import {faSync} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {ApplicableTableSettings, codesMoveTypeList} from '../../../../tableSettings';

import useFastStorage from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';

import ApplicationContext from '../../../../ApplicationContext';
import { Portal } from 'react-portal';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { Search } from '../../../../filtering';
import { CodeMoveTypeListItem } from '../../../../fms.data/models/CodeMoveTypeListItem';
import { JobNames } from '../../../../framework.core/JobNames';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import { ApiResult, InsertResult } from '../../../../framework.core/data/ApiResult';
import TextBox from '../../../../ui/TextBox';
import EditableTable, { CellData, EditableTableHandle, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import { FmsSecTask } from '../../../../fms.core/constants';
import GreenButton from '../../../../ui/GreenButton';
import { checkTabIsDirty } from '../../ReferenceFiles';
import { findSecurityTask } from '../../../../util';

import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import CloseButton from '../../../../ui/CloseButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';

function MoveTypeRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`moveType-${row.index}`} value={l.moveType} maxLength={15}
    onChange={e => changeLineItem(row.index, {moveType: e.target.value, isUpdate:true })} />
  );
}

function DescriptionRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`description-${row.index}`} 
    value={l.description} maxLength={100}
    onChange={e => changeLineItem(row.index, {description: e.target.value, isUpdate:true })} 
    onTabNext={() => {
      const focusElement = document.getElementById(`moveType-${row.index}`);
      if (focusElement) {
        focusElement.focus();
        return true;
      }
      return false;
    }}/>
  );
}

function formatElement(data:any) {
  const result = new CodeMoveTypeListItem();
  result.id = data.id;
  result.moveType = data.moveType;
  result.description = data.description;
  result.inactive = false;

  return result;
}

function formatData(data: Array<any>) {
  return data.map(d => formatElement(d));
}

function MoveTypes() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
    //ifmsReferenceFilesCancel: () => {},
  }});
  useFunctionKeys({onSave: handleSaveAndClose});
  
  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const tableRef = useRef(null);
  const masterTableRef = useRef<EditableTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState(Array<String>());
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, /*dataRef*/] = useFastStorage(`ifms-referenceFiles-movetype-list`, []);

  const savedDataRef = useRef<Array<CodeMoveTypeListItem>>([]);
  const oriData = useRef<CodeMoveTypeListItem[]>([]);
 // const addingToTheFirstLine = useRef(false);

  const tableHeight = 520;
  const systemSettings = useSystemSettings();
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeMoveType);
  const [focusFirstCell, setFocusFirstCell] = useState(true);

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }

  function setData(d:Array<any>, callByFetchData: boolean = false) {
    setFocusFirstCell(callByFetchData);
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeMoveTypeListItem, oriData.current, d));
  }

  async function fetchData(api:any) {
    setLoading(true);
    //<pt220816: portal
    //const apiResult = await api.getCodesMoveTypes();
    //setData(formatData(apiResult || []));
    const newSearch = { pageSize: systemSettings.defaultPageSize } as Search;
    const searchModel = CodeMoveTypeListItem.calculateSearchParameters(newSearch);
    const apiResult = await api.getListItems(JobNames.MoveType,
      'CodeMoveTypeListItem',
      searchModel,
      1,
      newSearch.pageSize);

      const apiResultforAll = await api.getListItems(JobNames.MoveType,
        'CodeMoveTypeListItem',
        searchModel,
        1);
    oriData.current = formatData(apiResult && apiResult.entities ? apiResult.entities : []);
    setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []), true);
    //pt220816>

    savedDataRef.current = apiResult && apiResult.entities ? apiResult.entities : [];
    if(apiResultforAll && apiResultforAll.entities){
      setAllData(apiResultforAll.entities.map((a: CodeMoveTypeListItem) => a.moveType.toLowerCase()));
    }
    
    setFocusFirstCell(false);
    setLoading(false);
    //setHasChanges(false);
  }

  useEffect(() => {
    if (data.length === 0)
      reload();
    else {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function reload() {
    fetchData(appContext.api);
  }

  async function cancelOperation(){
    setHasChanges(false);
    removeData();
    return true;
  }

  function getIndexOfFirstInvalidCell(rowIndex: number):number|undefined {
    if (!data) {
      return undefined;
    }

    if (!data[rowIndex].moveType) {
      return 1;
    }
    if (allData) { //기존데이터 목록
      const newData = [...allData];
      if(newData.filter(x => x === data[rowIndex].moveType.toLowerCase()).length === 0) // 현재 목록에 없음
      {
        newData.splice(rowIndex, 1, data[rowIndex].moveType.toLowerCase());
        setAllData(newData);
        return undefined;
      }else{
        if(newData[rowIndex] === data[rowIndex].moveType.toLowerCase()) return undefined;
        else{
          showGlobalOkDialog(<p>'{data[rowIndex].moveType}' is a duplicate value.</p>);
          return 1;
        }
      }
    } 
    else {
      const newData = [data[rowIndex].moveType.toLowerCase()];
      setAllData(newData);
    }
    return undefined;
  }

  function deleteRow(index: number): boolean {
    if (!data) {
      return false;
    }

    const element = data[index];

    // const wasNew = element.id <= 0;

    if (element.id > 0) {
      // setLoading(true);
      // const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem('CodeMoveTypeListItem', SQLType.Delete, _.omit(data[index], ['isNew']));

      // setLoading(false);
      // window.console.log(apiResult);

      let deleteRow = savedDataRef.current.find(x => x.id === element.id);
      if (deleteRow) {
        deleteRow.isDelete = true;
        //setHasChanges(true);
      }
    }

    data.splice(index, 1);

    setData(data);

    const tempData = data.map((a: CodeMoveTypeListItem) => a.moveType.toLowerCase());
    const newData = allData.splice(0, tempData.length, tempData);
    setAllData(newData);

    return true;
  }

  function applyToLineItem(rowIndex: number, change: Partial<CodeMoveTypeListItem>|null) {
    if (!change || !data) {
      return;
    }
    const checkData = data[rowIndex];
    if (!change.isNew && (change.moveType !== checkData.moveType || change.description !== checkData.description)) {
    _.merge(data[rowIndex], {..._.omit(change, ['id']), hasBeenChanged: true});
    //setHasChanges(true);
    setData(data);
    }
  }

  // function addingLineToFirstLine(){
  //   addingToTheFirstLine.current = true;
  //   masterTableRef.current?.add();
  // }

  function addLineItem() {
    const newItem = new CodeMoveTypeListItem();
    newItem.moveType = '';
    newItem.description = '';
    newItem.id = 0;
    newItem.inactive = false;
    newItem.isNew = true;

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: newItem
    };
  }

  async function duplicateCell(checkData: any) {
    if (!data) {
      return;
    }

    let duplicateFieldText = '';

    let isDuplicate = false;
    let insList = checkData.filter((x: any) => x.id <= 0);
    let chgList = checkData.filter((x: any) => x.id > 0 && x.hasBeenChanged === true);
    let focusRow = -1;

    if (insList.length > 0 && data.length > 0) {
      for (let i = 0; i < insList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (insList[i].moveType.toUpperCase() === data[j].moveType.toUpperCase()) {
            if (count === 0) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${insList[i].moveType}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (chgList.length > 0 && data.length > 0) {
      for (let i = 0; i < chgList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (chgList[i].moveType.toUpperCase() === data[j].moveType.toUpperCase()) {
            if (data[j].id > 0 && data[j].hasBeenChanged === true && tempRow === -1) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${chgList[i].moveType}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (!isDuplicate) {
      const apiResult = await appContext.api.checkDuplicatedKeys(JobNames.MoveType, 'CodeMoveTypeListItem', insList, chgList);
      if (apiResult && apiResult.success && apiResult.message) {
        duplicateFieldText = `${apiResult.message}`;
        isDuplicate = true;
      } else if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog('Could not check duplication.\r\nPlease try later.');
        return true;
      }
    }
    else
      await showGlobalOkDialog(`${duplicateFieldText}Please try after correcting duplication.`);

    return isDuplicate;
  }

  async function save() {
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.moveType);

    let errMsg = '';
    const dataToSave = validData.filter((x: any) => x.id <= 0 || x.hasBeenChanged === true); // new data is always last so index check works
    const dataToDelete = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload();
      return;
    }
    setLoading(true);

    for (let i = 0; i < dataToDelete.length; ++i) {
      const s = dataToDelete[i];
      const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem('CodeMoveTypeListItem', 
      SQLType.Delete, 
      _.omit(s, ['isDelete']));

      if(!apiResult || !apiResult.success)
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
    }


    if (dataToSave.length > 0) {
      const isDuplicate = await duplicateCell(dataToSave);
      if(isDuplicate){
        setLoading(false);
        return;
      }
      const cidData = dataToSave.map((a: CodeMoveTypeListItem) => a.cid?.toLowerCase()) as Array<String>;

      const secondSave = dataToSave.filter((x: CodeMoveTypeListItem) => cidData.includes(x.moveType.toLowerCase()));
      const firstSave = dataToSave.filter((x: CodeMoveTypeListItem) => !secondSave.includes(x));

      if (firstSave.length > 0) {
        const apiResult = await appContext.api.saveList('CodeMoveTypeListItem', firstSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
        }
        else {
          firstSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.moveType === x.moveType);
            if (index >= 0) {
              data[index].cid = x.moveType;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }

      if (secondSave.length > 0) {
        const apiResult = await appContext.api.saveList('CodeMoveTypeListItem', secondSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
        }
        else {
          secondSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.moveType === x.moveType);
            if (index >= 0) {
              data[index].cid = x.moveType;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
    }

    if(errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);

    // setLoading(false);
    // setHasChanges(false);
    reload();

    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-movetype-list',
    ref: tableRef,
    tableName: 'moveTypeList',
    ...codesMoveTypeList,
    height: `${tableHeight}px`,
    data: (data ?? []).map((x:any) => ({...x, changeLineItem: applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'moveType', EditCell: MoveTypeRenderer },
      { accessor: 'description', EditCell: DescriptionRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'moveType')
        return { type: TableCellTypes.Text, value: data[rowIndex].moveType };
      else if (accessor === 'description')
       return { type: TableCellTypes.Text, value: data[rowIndex].description };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
    },
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  //const moveTypeCount = data ? data.length : 0;

  return (
    <div style={{ marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Ocean <FontAwesomeIcon icon={faAngleRight} />&nbsp;Move Type</h1>
      </Portal> */}

      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element">
            <GreenButton size="regular" disabled={loading} onClick={save}>Save</GreenButton>
          </div>
          <div className="ifms-common-bar-element ifms-left-separator-light-gray">
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
          {/* <div className="ifms-common-bar-element">
            <BlackInverseButton name='list-screen-refresh' size="large" style={{marginTop: '-5px'}} disabled={loading} onClick={reload} data-tooltip="Refresh" >
              <FontAwesomeIcon color='#47C83E' icon={faSync} />
            </BlackInverseButton>
          </div> */}
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '432px' }}>
        <div style={{ marginLeft: 'auto' }}></div>

        <div style={{ position: 'relative', top: '-4px' }}>
          {//<SecondaryButton name='movetype-add-button' size="regular" disabled={!listSec?.grantAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          <BlackInverseButton name='list-screen-refresh' size="large" onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>

      <div style={{ width: '432px'}}>
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          ref={masterTableRef}
          tableOptions={tableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
          canDeleteRow={rowNumber => listSec?.grantDel ?? false}
        />
      </div>
    </div>
  );
}

export default MoveTypes;